import { useCallback, useEffect, useState } from 'react';

/**
 * Generic data hook for a SharePoint list module. Takes the module's listFn
 * (e.g. listSuppliers) and gives you { data, loading, error, refresh, setData }.
 *
 * Keeps optimistic-update ergonomics: mutations in child components can call
 * setData((prev) => ...) without re-fetching the whole list.
 */
export default function useSharePointList(listFn, opts) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listFn(opts);
      setData(rows);
    } catch (err) {
      setError(err);
      // eslint-disable-next-line no-console
      console.error('[useSharePointList]', err);
    } finally {
      setLoading(false);
    }
    // opts intentionally omitted: callers should memoize if they pass one
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listFn]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, loading, error, refresh, setData };
}
